import { Box } from '@material-ui/core';
import {
  AccountParticipationLevel,
  CashInCashOutMethod,
  CashInCashOutType,
  getCashInCashOutMethodById,
  getMerchantById,
  Merchant,
  patchUpdateCashInCashOutMethodByMerchantId,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { MerchantCashInCashOutMethodForm } from '@/components/modules/MerchantDetail/MerchantOverview/components/MerchantCashInCashOutMethod/components/MerchantCashInCashOutMethodForm';
import {
  MerchantCashInCashOutMethodSchema,
  MerchantCashInCashOutMethodType,
} from '@/components/modules/MerchantDetail/MerchantOverview/components/MerchantCashInCashOutMethod/schemas/MerchantCashInCashOutMethodSchema';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

interface MerchantCashInCashOutMethodEditDialogProps {
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog: () => void;
  merchant: Merchant;
  cashInCashOutMethod?: CashInCashOutMethod;
}

export const MerchantCashInCashOutMethodEditDialog = ({
  opened,
  setOpened,
  toggleDialog,
  merchant,
  cashInCashOutMethod,
}: MerchantCashInCashOutMethodEditDialogProps) => {
  const { merchantId } = useParams();

  if (!merchantId) return null;

  const { request, loading } = useRequest();
  const { revalidate: revalidateMerchant } = useCache(getMerchantById, { merchantId });
  const { revalidate: revalidateCashInCashOutMethod } = useCache(getCashInCashOutMethodById, {
    id: merchant.cashInCashOutMethodIds?.[0],
  });

  const merchantCashInCashOutMethodEditDialogDefaultValues = cashInCashOutMethod
    ? {
        accountHolderFirstName: cashInCashOutMethod.accountHolderFirstName,
        accountHolderLastName: `${cashInCashOutMethod.accountHolderFirstLastName} ${cashInCashOutMethod?.accountHolderSecondLastName}`,
        accountNumber: cashInCashOutMethod.accountNumber as string,
        description: cashInCashOutMethod.description,
      }
    : undefined;

  const onSubmitForm = async (useFormValues: MerchantCashInCashOutMethodType) => {
    const cashInCashOutMethodId = cashInCashOutMethod?.id ?? uuidv4();
    if (cashInCashOutMethod) {
      await request(patchUpdateCashInCashOutMethodByMerchantId, {
        id: merchant.id,
        cashInCashOutMethodId,
        ...useFormValues,
      });
    } else {
      await request(patchUpdateCashInCashOutMethodByMerchantId, {
        id: merchant.id,
        cashInCashOutMethodId,
        type: CashInCashOutType.BANK_ACCOUNT,
        accountHolderFirstName: useFormValues.accountHolderFirstName,
        accountHolderLastName: useFormValues.accountHolderLastName,
        accountHolderAccountParticipationLevel: AccountParticipationLevel.OWNER,
        accountNumber: useFormValues.accountNumber,
        description: useFormValues.description,
      });
      await revalidateMerchant();
      await revalidateCashInCashOutMethod();
    }
    revalidateCashInCashOutMethod();
    toggleDialog();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_CASH_IN_CASH_OUT_METHOD'}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      formSchema={MerchantCashInCashOutMethodSchema}
      defaultValues={merchantCashInCashOutMethodEditDialogDefaultValues}
    >
      <Box pt={2}>
        <MerchantCashInCashOutMethodForm
          cashInCashOutMethod={cashInCashOutMethod}
          fullWidth
          fieldVariant={FieldVariant.form}
        />
      </Box>
    </EditDialogWrapper>
  );
};
